import { createSlice } from '@reduxjs/toolkit'
import API, { API_VERSION, REQUEST_METHOD } from '../../../api'
import { OVERRIDE_COLUMNS_BY_SNAKE_KEY } from '../../../pages/feeManagement/constants/Columns'
import { OVERRIDE_TYPES, API_OVERRIDE_TYPE_MAPPING } from '../../../pages/feeManagement/constants/OverrideTypes'
import { createTrackedAsyncThunk } from '../../../pages/feeManagement/utils/reduxHelpers'

export const initialState = {
  overrideSheets: {
    items: [],
    isLoading: false,
    error: null,
  },
  overrideData: {
    data: null,
    isLoading: false,
    error: null,
  },
  createOverride: {
    isLoading: false,
    error: null,
    success: false,
    createdSheetId: null,
    result: null,
  },
  mappingData: {
    data: {
      products: [],
      panelists: [],
      state_and_counties_map: {},
      branches: [],
    },
    isLoading: false,
    error: null,
    fetchedEntityTypes: [],
  },
  updateOverride: {
    isLoading: false,
    error: null,
    success: false,
  },
}

// Helper function to determine override type from entity_types
const getOverrideType = (entityTypes) => {
  const sortedTypes = [...entityTypes].sort()
  const typeString = sortedTypes.join(',')

  if (typeString === API_OVERRIDE_TYPE_MAPPING.VENDOR_LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.VENDOR_LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.VENDOR.sort().join(',')) {
    return OVERRIDE_TYPES.VENDOR.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH_VENDOR_LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH_VENDOR_LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH_VENDOR.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH_VENDOR.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH_LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH_LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH.value
  }
  return null
}

export const fetchOverrideSheets = createTrackedAsyncThunk(
  'feeManagement/fetchOverrideSheets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API('overrides/sheets', API_VERSION.TWO).request(REQUEST_METHOD.GET)

      // Transform the response to include derived override type
      const transformedSheets = response.sheets.map((sheet) => {
        const overrideType = getOverrideType(sheet.entity_types)
        const transformed = {
          id: sheet.id,
          title: sheet.description,
          status: sheet.active ? 'active' : 'inactive',
          overrideType,
          overriddenFields: sheet.data_types.map((type) => OVERRIDE_COLUMNS_BY_SNAKE_KEY[type]?.columnKey || type),
          created: new Date(sheet.created).toLocaleDateString(),
          createdBy: sheet.created_by,
          lenderId: sheet.lender_id,
          // Store the original entity_types for reference
          entityTypes: sheet.entity_types,
        }
        return transformed
      })

      return transformedSheets
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to fetch override sheets')
    }
  },
  { errorSource: 'feeManagement.overrideSheets' },
)

export const fetchOverrideData = createTrackedAsyncThunk(
  'feeManagement/fetchOverrideData',
  async (sheetId, { rejectWithValue }) => {
    try {
      const api = API(`overrides/sheets/${sheetId}/overrides.json`, API_VERSION.TWO)
      const response = await api.request(REQUEST_METHOD.GET, {}, {}, true)
      return response
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to fetch override data')
    }
  },
  { errorSource: 'feeManagement.overrideData' },
)

export const updateOverrideSheet = createTrackedAsyncThunk(
  'feeManagement/updateOverrideSheet',
  async (payload, { rejectWithValue }) => {
    try {
      const { id, ...rest } = payload
      const response = await API(`overrides/sheets/${id}`, API_VERSION.TWO).request(REQUEST_METHOD.PATCH, {}, rest)
      return response
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to update override sheet')
    }
  },
  { errorSource: 'feeManagement.updateOverride' },
)

export const createOverrideSheet = createTrackedAsyncThunk(
  'feeManagement/createOverrideSheet',
  async (payload, { rejectWithValue }) => {
    try {
      const { description, entityTypes, dataTypes, overrides, active = false } = payload

      const requestBody = {
        description,
        entity_types: entityTypes,
        data_types: dataTypes,
        active,
        overrides,
      }

      const response = await API('overrides/sheets', API_VERSION.TWO).request(REQUEST_METHOD.POST, {}, requestBody)

      return response
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to create override sheet')
    }
  },
  {
    errorSource: 'feeManagement.createOverride',
    // Custom error handler to provide more context without the full payload
    customErrorContext: (payload) => ({
      description: payload?.description,
      entityTypes: payload?.entityTypes,
      dataTypes: payload?.dataTypes,
      overridesCount: payload?.overrides?.length || 0,
    }),
  },
)

export const fetchMappingData = createTrackedAsyncThunk(
  'feeManagement/fetchMappingData',
  async (entityTypes, { getState, rejectWithValue }) => {
    if (!entityTypes || !entityTypes.length) {
      return { newData: {}, newEntityTypes: [] }
    }

    // Filter out entity types we've already fetched
    const state = getState()
    const fetchedEntityTypes = state.feeManagement.mappingData.fetchedEntityTypes
    const newEntityTypes = entityTypes.filter((type) => !fetchedEntityTypes.includes(type))

    // If we've already fetched all these entity types, no need to make another request
    if (newEntityTypes.length === 0) {
      return { newData: {}, newEntityTypes: [] }
    }

    try {
      const queryString = `entity_types=${newEntityTypes.join(',')}`
      const response = await API(`overrides/mapping-data?${queryString}`, API_VERSION.TWO).request(REQUEST_METHOD.GET)

      return {
        newData: response,
        newEntityTypes,
      }
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to fetch mapping data')
    }
  },
  { errorSource: 'feeManagement.mappingData' },
)

const feeManagementSlice = createSlice({
  name: 'feeManagement',
  initialState,
  reducers: {
    resetFeeManagement: () => initialState,
    setOverrideData: (state, action) => {
      state.overrideData.data = action.payload
    },
    setOverrideDataLoading: (state, action) => {
      state.overrideData.isLoading = action.payload
    },
    setOverrideDataError: (state, action) => {
      state.overrideData.error = action.payload
    },
    resetCreateOverrideState: (state) => {
      state.createOverride = {
        isLoading: false,
        error: null,
        success: false,
        createdSheetId: null,
        result: null,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverrideSheets.pending, (state) => {
        state.overrideSheets.isLoading = true
        state.overrideSheets.error = null
      })
      .addCase(fetchOverrideSheets.fulfilled, (state, action) => {
        state.overrideSheets.items = action.payload
        state.overrideSheets.isLoading = false
        state.overrideSheets.error = null
      })
      .addCase(fetchOverrideSheets.rejected, (state, action) => {
        state.overrideSheets.isLoading = false
        state.overrideSheets.error = action.payload
      })
      .addCase(createOverrideSheet.pending, (state) => {
        state.createOverride.isLoading = true
        state.createOverride.error = null
        state.createOverride.success = false
      })
      .addCase(createOverrideSheet.fulfilled, (state, action) => {
        state.createOverride.isLoading = false
        state.createOverride.success = true
        state.createOverride.createdSheetId = action.payload.sheet_id
        state.createOverride.result = {
          sheetId: action.payload.sheet_id,
          updatedCount: action.payload.updated_count || 0,
          insertedCount: action.payload.inserted_count || 0,
        }
      })
      .addCase(createOverrideSheet.rejected, (state, action) => {
        state.createOverride.isLoading = false
        state.createOverride.error = action.payload
        state.createOverride.success = false
      })
      .addCase(fetchMappingData.pending, (state) => {
        state.mappingData.isLoading = true
        state.mappingData.error = null
      })
      .addCase(fetchMappingData.fulfilled, (state, action) => {
        const { newData, newEntityTypes } = action.payload

        // If there's no new data, just update loading state
        if (!newData.products && !newData.panelists && !newData.state_and_counties_map && !newData.branches) {
          state.mappingData.isLoading = false
          return
        }

        // Merge products
        if (newData.products) {
          const existingProductIds = new Set(state.mappingData.data.products.map((p) => p.id))
          const newProducts = newData.products.filter((p) => !existingProductIds.has(p.id))
          state.mappingData.data.products = [...state.mappingData.data.products, ...newProducts]
        }

        // Merge panelists
        if (newData.panelists) {
          const existingPanelistIds = new Set(state.mappingData.data.panelists.map((p) => p.id))
          const newPanelists = newData.panelists.filter((p) => !existingPanelistIds.has(p.id))
          state.mappingData.data.panelists = [...state.mappingData.data.panelists, ...newPanelists]
        }

        // Merge state_and_counties_map
        if (newData.state_and_counties_map) {
          state.mappingData.data.state_and_counties_map = {
            ...state.mappingData.data.state_and_counties_map,
            ...newData.state_and_counties_map,
          }
        }

        // Merge branches
        if (newData.branches) {
          const existingBranchIds = new Set(state.mappingData.data.branches.map((b) => b.id))
          const newBranches = newData.branches.filter((b) => !existingBranchIds.has(b.id))
          state.mappingData.data.branches = [...state.mappingData.data.branches, ...newBranches]
        }

        // Add new entity types to the fetched list
        state.mappingData.fetchedEntityTypes = [...state.mappingData.fetchedEntityTypes, ...newEntityTypes]
        state.mappingData.isLoading = false
      })
      .addCase(fetchMappingData.rejected, (state, action) => {
        state.mappingData.isLoading = false
        state.mappingData.error = action.payload
      })
      .addCase(updateOverrideSheet.pending, (state) => {
        state.updateOverride.isLoading = true
        state.updateOverride.error = null
      })
      .addCase(updateOverrideSheet.fulfilled, (state, action) => {
        state.updateOverride.isLoading = false
        state.updateOverride.error = null
        state.updateOverride.success = true
      })
      .addCase(updateOverrideSheet.rejected, (state, action) => {
        state.updateOverride.isLoading = false
        state.updateOverride.error = action.payload
        state.updateOverride.success = false
      })
      .addCase(fetchOverrideData.pending, (state) => {
        state.overrideData.isLoading = true
        state.overrideData.error = null
      })
      .addCase(fetchOverrideData.fulfilled, (state, action) => {
        state.overrideData.isLoading = false
        state.overrideData.data = action.payload
        state.overrideData.error = null
      })
      .addCase(fetchOverrideData.rejected, (state, action) => {
        state.overrideData.isLoading = false
        state.overrideData.error = action.payload
      })
  },
})

export const {
  resetFeeManagement,
  setOverrideData,
  setOverrideDataLoading,
  setOverrideDataError,
  resetCreateOverrideState,
} = feeManagementSlice.actions

export default feeManagementSlice.reducer
